import { Component, computed, effect, inject, Input, signal } from '@angular/core';
import {
  BuildingFilterDto,
  RepairTaskItemDto,
  RepairTaskService,
  SortDto
} from '@artemis-software/wr-api';
import { Tracked } from '@/utils/signals/tracked';
import { loader } from '@/utils/signals/loader';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs';
import TenantTypeEnum = BuildingFilterDto.TenantTypeEnum;
import { startOfToday } from 'date-fns';

@Component({
  standalone: false,
  selector: 'wr-tenant-dashboard-repair-task-overview',
  templateUrl: './tenant-dashboard-repair-task-overview.component.html',
  styleUrls: ['./tenant-dashboard-repair-task-overview.component.scss'],
})
export class TenantDashboardRepairTaskOverviewComponent {
  repairTaskService = inject(RepairTaskService);
  formBuilder = inject(FormBuilder);

  @Input()
  @Tracked()
  tenantId!: string;

  @Tracked() page = 0;
  @Tracked() pageSize = 10;
  @Tracked() sort: SortDto = { sort: 'created', order: 'DESC' };

  isLoading = loader();

  reload = signal(0);

  form!: FormGroup;

  result = asyncComputed(() => {
    if (!this.tenantId) {
      return undefined;
    }
    this.reload();
    return this.repairTaskService.findAllPaginated({
      tenantId: this.tenantId,
      page: this.page,
      size: this.pageSize,
      sort: {
        sort: this.sort.sort,
        order: this.sort.order,
      },
      status: this.form.get('status')?.value ?? undefined,
      title: this.form.get('title')?.value ?? undefined,
      contractorId: this.form.get('contractorId')?.value ?? undefined,
      selectedRange: {
        start: startOfToday().toISOString(),
        end: this.form.get('deadline')?.value ?? undefined,
      }
    });
  }, undefined, this.isLoading);

  repairTasks = computed(() => this.result()?.items ?? []);

  count = computed(() => this.result()?.totalItems ?? 0);

  constructor() {
    this.form = this.formBuilder.group({
      title: this.formBuilder.control(''),
      status: this.formBuilder.control(null),
      deadline: this.formBuilder.control(null),
      contractorId: this.formBuilder.control(null),
    });
    effect(() => {
      this.form.valueChanges.pipe(debounceTime(200)).subscribe(() => {
        this.reload.update((n) => n + 1);
      });
    });
  }

  readonly getRepairTaskLink = (repairTask: RepairTaskItemDto) => (['inspection', repairTask.inspectionEntry!.inspectionId]);
  protected readonly TenantTypeEnum = TenantTypeEnum;
}
